/* <kenra-article-body> — Kenra blog article body video fix (WEB-6319) + VPPA gating (WEB-6442).
 *
 * The Contentful→Shopify blog migration left inline videos double-encoded in
 * `article.content`: each video appears once as a literal
 * `[[video]]={"link":"…"}` shortcode paragraph — sitting in its correct spot in
 * the flow but rendered as dead literal text — and again as a real responsive
 * <iframe>, with all the iframes clumped together at the end of the body. The
 * marketing site (Gatsby) expanded the shortcode in place at runtime; this
 * element reproduces that so the videos land where the author placed them:
 *
 *   1. Replace each `[[video]]` shortcode paragraph with a gated poster.
 *   2. Remove the duplicated trailing iframe for any video we expanded in place,
 *      so each video renders once, in the right spot.
 *   3. Inject the real iframe only when a consented visitor clicks the poster.
 *
 * WEB-6442 (AC 6) — WHY A POSTER AND NOT AN IFRAME. This used to build the
 * iframe directly with a live `src`. Inserting that into the document starts the
 * YouTube request immediately, disclosing which video the visitor is about to
 * watch before any consent — the same VPPA exposure as WEB-6441, reached through
 * a different door. The MutationObserver in vppa-consent.js does NOT save it:
 * removing `src` after insertion does not cancel a request that has already
 * started (measured directly — docs/kenra-vppa-handling.md §3c). So the fix has to be to never put a
 * live `src` in the DOM until consent exists.
 *
 * Instead each shortcode expands to the theme's standard gated-poster contract —
 * `img.kenra-vppa-poster[data-vppa-yt-id]` carrying only the opaque id (mirroring
 * snippets/kenra-video-poster.liquid) inside a `[data-kenra-video-trigger]`
 * button. That hands both states to the machinery that already exists:
 *
 *   un-consented — vppa-consent.js gates the poster (placeholder overlay, the
 *                  playbook §7b copy, the preference-centre link) and its
 *                  capture-phase click handler turns a click into "open the
 *                  OneTrust preference centre", calling stopImmediatePropagation
 *                  so the handler below never runs. No YouTube request fires.
 *   consented    — vppa-consent.js reveals the real thumbnail, the capture
 *                  handler stands down, and the click handler below swaps in the
 *                  iframe. The request happens with consent in place, on a
 *                  deliberate user action.
 *
 * Fail-safe: if window.KenraVPPA is absent (the app embed isn't enabled on this
 * store — the documented dev/qa state, see docs/kenra-vppa-handling.md §5) the
 * click is a no-op. Consistent with the rest of the gate: never show video
 * without a confirmed grant.
 *
 * Degrades safely: an article with only real iframes (no shortcode) is left
 * untouched — those are server-rendered and are fixed at the source by
 * scripts/neutralize-article-video-embeds.ts, since no client-side code can
 * un-fire their request. A shortcode whose link yields no YouTube id is left
 * as-is. */
if (!customElements.get('kenra-article-body')) {
  customElements.define(
    'kenra-article-body',
    class KenraArticleBody extends HTMLElement {
      connectedCallback() {
        const SHORTCODE = /^\s*\[\[video\]\]\s*=\s*(\{[\s\S]*\})\s*$/;

        // The same inert 1x1 transparent GIF snippets/kenra-video-poster.liquid emits, and the value
        // vppa-consent.js resets to on revoke. Must stay byte-identical to both.
        const PLACEHOLDER_SRC =
          'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

        // Pull the YouTube id out of an embed/watch/short URL. Returns '' for
        // non-YouTube URLs and for the `videoseries` playlist placeholder,
        // which isn't a playable single-video id.
        const youTubeId = (url) => {
          if (!url) return '';
          const match = String(url).match(
            /(?:youtube(?:-nocookie)?\.com\/(?:embed\/|watch\?v=)|youtu\.be\/)([\w-]{6,})/
          );
          const id = match ? match[1] : '';
          return id === 'videoseries' ? '' : id;
        };

        const videoConsented = () => {
          try {
            return !!(window.KenraVPPA && window.KenraVPPA.consented());
          } catch {
            return false;
          }
        };

        // The gated poster + play trigger. Deliberately mirrors
        // snippets/kenra-video-poster.liquid: only the opaque id, never a youtube URL.
        const buildGatedPoster = (id, title) => {
          const wrap = document.createElement('div');
          wrap.className = 'kenra-article__video';
          // Declare the overlay mount (WEB-6442 AC 2). `gateHost()` in vppa-consent.js treats an
          // undeclared host as `cta: false`, which would make blog in-body video the one gated
          // surface with no §7b preference-centre link in its overlay. This wrapper is exactly the
          // shape the contract wants: it wraps the trigger and nothing else.
          wrap.setAttribute('data-kenra-vppa-mount', '');

          const trigger = document.createElement('button');
          trigger.type = 'button';
          trigger.className = 'kenra-article__video-trigger';
          trigger.setAttribute('data-kenra-video-trigger', '');
          trigger.setAttribute('data-youtube-id', id);
          trigger.setAttribute('aria-label', title ? `Play video: ${title}` : 'Play video');
          // The real title, stashed verbatim. Recovering it by string-stripping the formatted
          // aria-label was fragile in two directions: with no source title the label is the bare
          // "Play video" (no colon), the strip regex no-oped, and that truthy string beat
          // buildFrame's 'YouTube video player' fallback; and vppa-consent.js's
          // describeGatedTrigger() rewrites this same aria-label while gated, so a click could name
          // the player after the gating copy. A dedicated attribute is immune to both.
          if (title) trigger.setAttribute('data-video-title', title);

          const poster = document.createElement('img');
          poster.className = 'kenra-vppa-poster kenra-article__video-poster';
          poster.src = PLACEHOLDER_SRC;
          poster.setAttribute('data-vppa-yt-id', id);
          poster.setAttribute('data-vppa-res', 'maxresdefault');
          poster.width = 1280;
          poster.height = 720;
          // The real alt, not alt="" — this file must work against either extension version. The
          // current vppa-consent.js stashes a populated alt into data-vppa-alt before clearing it
          // (WEB-6442 AC 9), so gating still ends up correct; an older extension simply leaves it.
          poster.alt = title || '';
          poster.loading = 'lazy';
          poster.decoding = 'async';

          const play = document.createElement('span');
          play.className = 'kenra-article__video-play';
          play.setAttribute('aria-hidden', 'true');

          trigger.appendChild(poster);
          trigger.appendChild(play);
          wrap.appendChild(trigger);
          return wrap;
        };

        // The real embed. Only ever called from the click handler below, i.e. only once consent is
        // confirmed — never at render time.
        const buildFrame = (id, title) => {
          const frame = document.createElement('iframe');
          frame.className = 'kenra-article__video-frame';
          frame.src = `https://www.youtube.com/embed/${encodeURIComponent(id)}?autoplay=1&rel=0`;
          frame.title = title || 'YouTube video player';
          frame.setAttribute('frameborder', '0');
          // Kept byte-identical to the list in assets/kenra-video-modal.js so an in-body video and a
          // modal video expose the same player features. The narrower list this file used to carry
          // (inherited from before WEB-6442) omitted `fullscreen` and `web-share`, so the same YouTube
          // video behaved differently depending on which surface opened it.
          frame.setAttribute(
            'allow',
            'accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share'
          );
          frame.setAttribute('allowfullscreen', '');
          return frame;
        };

        // Snapshot the iframes that shipped in the body BEFORE we add any, and
        // map id → title so the in-place posters keep the real accessible name.
        //
        // BOTH forms have to match, and THIS SELECTOR IS THE GUARD — not a belt-and-braces extra.
        // `scripts/neutralize-article-video-embeds.ts` rewrites server-rendered iframes to a src-less
        // form carrying `data-vppa-src`, including in the four `[[video]]` articles. An `[src]`-only
        // selector stops seeing those, so the trailing duplicate survives de-duplication AND
        // `restoreAll()` later promotes it to a live player with no click — the video twice over, the
        // click gate bypassed.
        //
        // An earlier revision had the script skip shortcode articles whole and called THAT the guard.
        // It was removed because it left those four articles leaking at parse time. Do not restore
        // it; keep this selector correct instead. Matching the attribute's PRESENCE rather than
        // "/embed/" in its value also covers a stashed `youtu.be` URL, which contains no "/embed/".
        // Match on the ATTRIBUTE'S PRESENCE, not on "/embed/" being in its value. A stashed
        // `data-vppa-src="https://youtu.be/<id>"` contains no `/embed/`, so a `*="/embed/"` selector
        // silently skipped it — the duplicate survived and restoreAll() promoted it to a live player
        // with no click. youTubeId() below is the real filter and returns '' for anything that is not
        // a YouTube URL, so casting wide here costs nothing and cannot over-match.
        const original = Array.from(this.querySelectorAll('iframe[src], iframe[data-vppa-src]'));
        const srcOf = (frame) => frame.getAttribute('src') || frame.getAttribute('data-vppa-src');
        const titleById = new Map();
        original.forEach((frame) => {
          const id = youTubeId(srcOf(frame));
          if (id && frame.title && !titleById.has(id)) titleById.set(id, frame.title);
        });

        // 1. Expand each `[[video]]` shortcode paragraph in place, as a gated poster.
        const expanded = new Set();
        this.querySelectorAll('p').forEach((p) => {
          const payload = (p.textContent || '').match(SHORTCODE);
          if (!payload) return;
          let link;
          try {
            link = JSON.parse(payload[1]).link;
          } catch {
            return; // malformed payload — leave the paragraph untouched
          }
          const id = youTubeId(link);
          if (!id) return;
          p.replaceWith(buildGatedPoster(id, titleById.get(id)));
          expanded.add(id);
        });

        // 2. Remove the now-duplicated original iframe(s) for each video we
        // re-created in place. Only the originals snapshotted above are
        // candidates, so the freshly built posters are never touched. Drop the
        // iframe's wrapper only when it's a <div> wrapping *just* this iframe
        // (the migrated `<div style="padding-bottom:56.25%">…</div>` case);
        // otherwise remove the iframe alone — never walk up to a shared
        // container like `.kenra-article__body`, which is itself a <div>.
        original.forEach((frame) => {
          const id = youTubeId(srcOf(frame));
          if (!id || !expanded.has(id)) return;
          const wrapper = frame.parentElement;
          if (wrapper && wrapper !== this && wrapper.tagName === 'DIV' && wrapper.children.length === 1) {
            wrapper.remove();
          } else {
            frame.remove();
          }
        });

        // 3. Play on click — but only with consent. When un-consented, vppa-consent.js's
        // capture-phase handler has already called stopImmediatePropagation() and opened the
        // preference centre, so this never runs; the consent re-check is defence in depth for the
        // case where the extension isn't present at all.
        //
        // `[data-kenra-video-trigger]` HAS THREE INDEPENDENT LISTENERS. Changing any one of them
        // without checking the others will silently break a surface:
        //   1. vppa-consent.js — capture phase, document level. Runs FIRST and calls
        //      stopImmediatePropagation() while un-consented, which is what keeps 2 and 3 from
        //      firing pre-consent.
        //   2. kenra-video-modal.js — bubble phase. Opens the shared lightbox, but BAILS when the
        //      trigger has no `data-modal-target`.
        //   3. this handler — bubble phase, scoped to one <kenra-article-body>.
        // 2 and 3 coexist only because of that `data-modal-target` bail: the posters built here
        // deliberately omit the attribute, so the modal handler ignores them and plays in place
        // instead. That is a real contract, not a coincidence — if in-body video ever needs the
        // lightbox, give it `data-modal-target` and DELETE this handler rather than running both.
        if (!expanded.size) return;
        this.addEventListener('click', (event) => {
          const trigger = event.target.closest?.('[data-kenra-video-trigger]');
          if (!trigger || !this.contains(trigger)) return;
          if (!videoConsented()) return;
          const id = trigger.getAttribute('data-youtube-id');
          if (!id) return;
          const wrap = trigger.parentElement;
          if (!wrap || !wrap.classList.contains('kenra-article__video')) return;
          event.preventDefault();
          // Empty string when absent, so buildFrame's 'YouTube video player' fallback actually runs.
          const title = trigger.getAttribute('data-video-title') || '';
          wrap.replaceChildren(buildFrame(id, title));
        });
      }
    }
  );
}
